import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import FacetDataContext from '~/contexts/FacetDataContext';
import type {
  AppliedFilters,
  MakesFacet,
  MakeType,
  TrimFacet
} from '~/contracts';
import Checkbox from './Checkbox';
import FilterDrawer from './FilterDrawer';
import LabelWithCount from './LabelWithCount';
import { CommonWrapper, TrimWrapper } from './styles';
import WithClassName from './WithClassName';

type SelectedModel = {
  make: string;
  model: string;
  trims?: Array<TrimFacet>;
};

function useAppliedMakes(appliedFilters: AppliedFilters): MakeType[] {
  return useMemo(
    () =>
      (appliedFilters.makes ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    [appliedFilters.makes]
  );
}

function useFacetDataForSelectedModels(
  appliedMakes: Array<MakeType>,
  makes: MakesFacet = {}
): SelectedModel[] {
  return useMemo(
    () =>
      appliedMakes.flatMap(appliedMake => {
        const make = makes[appliedMake.name];

        return make && appliedMake.models
          ? appliedMake.models
              .flatMap(appliedModel => {
                const model = make.models.find(
                  m => m.key === appliedModel.name
                );

                return model
                  ? {
                      make: make.key,
                      model: model.key,
                      trims: model.trims
                    }
                  : [];
              })
              .sort((a, b) => a.model.localeCompare(b.model))
          : [];
      }),
    [appliedMakes, makes]
  );
}

function getIsSelected(
  makes: MakeType[],
  makeName: string,
  modelName: string,
  trim: string
): boolean {
  const make = makes?.find(m => m.name === makeName);
  const model = make?.models?.find(m => m.name === modelName);

  return model?.trims?.includes(trim) ?? false;
}

function Trim({ className }: WithClassName) {
  const [expand, setExpand] = useState(false);
  const { appliedFilters, updateMakes } = useContext(AppliedFiltersContext);
  const { facetData } = useContext(FacetDataContext);

  const appliedMakes = useAppliedMakes(appliedFilters);
  const selectedModels = useFacetDataForSelectedModels(
    appliedMakes,
    facetData.makes
  );

  const includeMakeInDisplay = appliedMakes.length > 1;
  const show = selectedModels.length > 0;

  useEffect(() => {
    if (!show) setExpand(false);
  }, [show]);

  return (
    <AnimateHeight duration={500} height={show ? 'auto' : 0}>
      <FilterDrawer
        className={className}
        dataQa="facet-trims"
        facetContent={appliedMakes}
        facetName="Trim"
        show={expand}
        onToggle={useCallback(() => setExpand(v => !v), [])}
      >
        <CommonWrapper>
          {selectedModels.map(({ make, model, trims }) => (
            <TrimWrapper key={`${model}-trims`}>
              <h4 className="t-header-s">
                {includeMakeInDisplay ? make : ''} {model}
              </h4>
              {trims?.map(({ key: trim, count }) => (
                <Checkbox
                  checked={getIsSelected(appliedMakes, make, model, trim)}
                  className="pl-4"
                  facet="trims"
                  filter={trim}
                  id={`trim-${model}-${trim}`}
                  key={trim}
                  onChange={() =>
                    updateMakes({ name: trim, parents: [make, model] })
                  }
                  label={<LabelWithCount count={count} displayName={trim} />}
                />
              ))}
            </TrimWrapper>
          ))}
        </CommonWrapper>
      </FilterDrawer>
    </AnimateHeight>
  );
}

export default Trim;
